// src/pages/auth/ForgotPasswordPage.tsx
import React, { useLayoutEffect, useRef, useState } from "react";

import { useAuth } from "features/auth/hooks/useAuth";
import ForgotPasswordForm, { ForgotFormData } from "features/auth/components/ForgotPasswordForm";
import { getErrorMessage } from "utils/errors";

type LineGeom = {
  left: number;
  titleTop: number;
  titleBottom: number;
  containerTop: number;
  containerBottom: number;
};

const ForgotPasswordPage: React.FC = () => {
  const { sendResetEmail } = useAuth();
  const [authError, setAuthError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Title/container vertical line geometry (CookiesPage logic)
  const pageRef = useRef<HTMLElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [geom, setGeom] = useState<LineGeom | null>(null);

  useLayoutEffect(() => {
    const page = pageRef.current;
    const title = titleRef.current;
    const container = containerRef.current;
    if (!page || !title || !container) return;

    const compute = () => {
      const p = page.getBoundingClientRect();
      const t = title.getBoundingClientRect();
      const c = container.getBoundingClientRect();

      const left = Math.round(t.left + t.width / 2 - p.left);

      setGeom({
        left: Math.max(0, left),
        titleTop: Math.max(0, Math.round(t.top - p.top)),
        titleBottom: Math.max(0, Math.round(t.bottom - p.top)),
        containerTop: Math.max(0, Math.round(c.top - p.top)),
        containerBottom: Math.max(0, Math.round(c.bottom - p.top))
      });
    };

    compute();

    const ro = new ResizeObserver(() => compute());
    ro.observe(page);
    ro.observe(title);
    ro.observe(container);

    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, []);

  const handleSend = async (data: ForgotFormData) => {
    setAuthError("");
    setSuccessMessage("");
    try {
      await sendResetEmail(data.email);
      setSuccessMessage("If that email is registered, a reset link has been sent.");
    } catch (err: any) {
      setAuthError(getErrorMessage(err, "Failed to send reset email"));
    }
  };

  const TITLE_GAP_PX = 14;
  const CONTAINER_TOP_MARGIN_CLASS = "mt-16 sm:mt-20";

  return (
    <main ref={pageRef} className="relative min-h-screen px-4 py-12 sm:py-16">
      {/* Vertical gold line segments (with gaps for title + container) */}
      {geom && (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: 0,
              bottom: `calc(100% - ${Math.max(0, geom.titleTop - TITLE_GAP_PX)}px)`
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: geom.titleBottom + TITLE_GAP_PX,
              bottom: `calc(100% - ${geom.containerTop}px)`
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: geom.containerBottom,
              bottom: 0
            }}
          />
        </>
      )}

      <div className="relative z-10">
        {/* Title */}
        <header className="text-center">
          <h1
            ref={titleRef}
            className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#ceddf2]"
          >
            Reset password
          </h1>
        </header>

        {/* White container (middle 3/5 on desktop) */}
        <section className={[CONTAINER_TOP_MARGIN_CLASS, "flex justify-center"].join(" ")}>
          <div
            ref={containerRef}
            className="w-full md:w-3/5 bg-white rounded-2xl shadow-sm border border-slate-200 px-6 py-8 sm:px-8"
          >
            <ForgotPasswordForm
              onSubmit={handleSend}
              authError={authError}
              successMessage={successMessage}
            />
          </div>
        </section>
      </div>
    </main>
  );
};

export default ForgotPasswordPage;
